import {computed, nextTick, ref, useTemplateRef, watch} from "vue";
import {QuestionFieldsPresenter} from "@/utils/presenters/QuestionFieldsPresenter.ts";
import {QInput} from "quasar";
import GUIUtilities from "@/utils/common/GUIUtilities.ts";
import ClientCache from "@/utils/login/ClientCache.ts";
import {StringUtils} from "@/utils/common/StringUtils.ts";
import SearchingAndSuggestionUtil from "@/utils/editView/widgets/SearchingAndSuggestionUtil.ts";
import Translator from "@/utils/common/Translator.ts";
import FieldType from "@/utils/constants/FieldType.ts";
import {useErrorsStore} from "@/stores/ToastDataStore.ts";
import ObjectChecker from "@/utils/common/ObjectChecker.ts";
import QuestionField = namaTypes.QuestionField;
import DTOFilledQuestionField = namaTypes.DTOFilledQuestionField;
import FieldActionContextInfo = namaTypes.FieldActionContextInfo;
import AllowedValue = namaTypes.AllowedValue;

export type MenuItem = { id: string; code: string; caption: string; value: any; searchValue: string };

export function useQuestionList(presenter: QuestionFieldsPresenter, questionField: QuestionField) {
  const fieldId: string = questionField?.id ?? '';

  const selectedDetails = computed<any[]>(() => {
    const details = presenter?.record.value[fieldId]?.[QuestionFieldsPresenter.detailId];
    if (details?.length > 1 || details?.[0]?.[fieldId])
      return details;
    return [];
  });
  const selectedRowsNumber = computed(() => selectedDetails.value.length);

  function getDetailText(detail: any): string {
    const value = detail?.[fieldId];
    if (ObjectChecker.isNullOrUndefined(value))
      return '';
    if (ObjectChecker.isObject(value))
      return ObjectChecker.getFirstNotEmptyObj(value.description, value.code, value.text, value.value) ?? '';
    return String(value);
  }

  function removeDetail(detail: any) {
    const details: any[] = presenter.record.value[fieldId]?.[QuestionFieldsPresenter.detailId] ?? [];
    const idx = details.findIndex(d => d.id === detail.id);
    if (idx >= 0)
      details.splice(idx, 1);
  }

  const searchRef = useTemplateRef<QInput>("searchRef");
  const searchText = ref('');
  const suggestItems = ref<MenuItem[]>([]);
  const isLoading = ref(false);
  let suggestSeq = 0;
  let searchDebounceTimer: number | undefined;

  const allowedItems = computed<MenuItem[]>(() => {
    if (ObjectChecker.isNotEmptyOrNull(questionField.allowedValues)) {
      return questionField.allowedValues.map((a: AllowedValue) => {
        const id = a.id ?? a.value ?? '';
        const caption = a.title ? Translator.translateNaMaText(a.title) : Translator.translateId(id);
        return {id, code: id, caption, value: id, searchValue: (caption + ' ' + id).toLowerCase()};
      });
    }
    const md = questionField.metaData;
    if (!md)
      return [];
    const values = GUIUtilities.getAllowedValues(presenter.entityType, md);
    const enumName = md.enumTypeName ?? '';
    return (values ?? []).map(v => {
      const caption = enumName ? Translator.translateId(enumName + '.' + v) : Translator.translateId(v);
      return {id: v, code: v, caption, value: v, searchValue: (caption + ' ' + v).toLowerCase()};
    });
  });

  const useSuggestApi = computed(() =>
    ObjectChecker.isEmptyOrNull(questionField.allowedValues) && allowedItems.value.length === 0
  );

  const filteredAllowedItems = computed<MenuItem[]>(() => {
    const q = searchText.value?.trim().toLowerCase();
    if (!q)
      return allowedItems.value;
    const normalized = StringUtils.normalizeArabic(q);
    return allowedItems.value.filter(i =>
      i.searchValue.includes(q) || i.searchValue.includes(normalized)
    );
  });

  const displayedItems = computed<MenuItem[]>(() => useSuggestApi.value ? suggestItems.value : filteredAllowedItems.value);
  const fieldTitle = computed(() => Translator.translateTitledId(presenter.entityType, questionField));

  function resolveReferencedEntityType(): string | undefined {
    let referencedEntityType: string | undefined = questionField.metaData?.referencedEntityType;
    if (referencedEntityType && referencedEntityType.startsWith('$')) {
      const paramName: string = referencedEntityType.substring(1);
      const filled: DTOFilledQuestionField | undefined = presenter.getQuestionFields().find(q => paramName === q.id);
      referencedEntityType = filled ? filled.value : "";
    }
    return referencedEntityType;
  }

  async function fetchSuggestions(query: string) {
    const md = questionField.metaData;
    if (!md)
      return;
    const seq = ++suggestSeq;
    isLoading.value = true;
    try {
      const referencedEntityType = resolveReferencedEntityType() ?? '';
      const ownerEntityType = presenter.entityType;
      const contextInfo: FieldActionContextInfo = presenter.createSearchContextInfo(fieldId);
      const extraCriteria = presenter.getDefaultCriteria(fieldId);
      const results = await SearchingAndSuggestionUtil.searchFor({
        ownerEntityType,
        referencedEntityType,
        fieldId,
        contextInfo,
        query,
        extraCriteria,
      });
      if (seq !== suggestSeq) return;
      suggestItems.value = (results ?? []).map(r => {
        const code = r.code ?? '';
        const caption = r['optionCaption'] ?? Translator.getNameForTitle(r) ?? '';
        return {
          id: r.id ?? code,
          code,
          caption,
          value: r,
          searchValue: (code + ' ' + caption).toLowerCase(),
        };
      });
    } finally {
      if (seq === suggestSeq) isLoading.value = false;
    }
  }

  watch(searchText, (q) => {
    if (!useSuggestApi.value) return;
    if (searchDebounceTimer) window.clearTimeout(searchDebounceTimer);
    searchDebounceTimer = window.setTimeout(() => fetchSuggestions(q ?? ''), 250);
  });

  function isItemSelected(item: MenuItem): boolean {
    return selectedDetails.value.some(d => {
      return ObjectChecker.areEqual(d?.[fieldId], item.value)
    });
  }

  const allSelected = computed(() => displayedItems.value.length > 0 && displayedItems.value.every(i => isItemSelected(i)));
  const someSelected = computed(() => !allSelected.value && displayedItems.value.some(i => isItemSelected(i)));

  async function toggleAll() {
    const record = presenter.record.value[fieldId] ?? (presenter.record.value[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const details: any[] = record[QuestionFieldsPresenter.detailId] ?? (record[QuestionFieldsPresenter.detailId] = []);
    if (allSelected.value) {
      const displayed = new Set(displayedItems.value.map(i => i.id));
      for (let idx = details.length - 1; idx >= 0; idx--) {
        const stored = details[idx]?.[fieldId];
        const storedId = stored == null ? '' : typeof stored === 'object' ? String(stored.id ?? stored.code ?? '') : String(stored);
        if (displayed.has(storedId))
          details.splice(idx, 1);
      }
    } else {
      for (const item of displayedItems.value) {
        if (!isItemSelected(item)) {
          const storedValue = useSuggestApi.value ? buildReferenceValue(item) : item.id;
          details.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: storedValue});
        }
      }
    }
  }

  async function toggleItem(item: MenuItem) {
    const record = presenter.record.value[fieldId] ?? (presenter.record.value[fieldId] = {[QuestionFieldsPresenter.detailId]: []});
    const details: any[] = record[QuestionFieldsPresenter.detailId] ?? (record[QuestionFieldsPresenter.detailId] = []);
    const idx = details.findIndex(d => {
      return ObjectChecker.areEqual(d?.[fieldId], item.value)
    });
    if (idx >= 0) {
      details.splice(idx, 1);
    } else {
      const storedValue = useSuggestApi.value ? buildReferenceValue(item) : item.id;
      details.push({id: await ClientCache.asyncNextGUIDId(), [fieldId]: storedValue});
    }
  }

  function buildReferenceValue(item: MenuItem): any {
    const r = item.value ?? {};
    return {
      entityType: r.entityType ?? resolveReferencedEntityType(),
      id: r.id ?? item.id,
      code: r.code ?? item.code,
      altCode: r.altCode,
      name1: r.name1,
      name2: r.name2,
      description: r['optionCaption'] ?? Translator.getNameForTitle(r) ?? item.caption,
    };
  }

  function isReferenceWithoutEntityType(): boolean {
    return questionField.metaData?.fieldType === FieldType.Reference && !resolveReferencedEntityType();
  }

  async function onPlusClick(e: Event) {
    if (isReferenceWithoutEntityType()) {
      useErrorsStore().pushErrorStr("You must select Entity Type");
      return;
    }
    searchText.value = '';
    if (useSuggestApi.value)
      await fetchSuggestions('');
    await nextTick(() => searchRef.value?.focus());
  }

  async function loadItems() {
    if (isReferenceWithoutEntityType()) return;
    if (useSuggestApi.value)
      await fetchSuggestions('');
  }

  return {
    fieldId,
    selectedDetails,
    selectedRowsNumber,
    getDetailText,
    removeDetail,
    searchRef,
    searchText,
    suggestItems,
    isLoading,
    allowedItems,
    useSuggestApi,
    filteredAllowedItems,
    displayedItems,
    fieldTitle,
    resolveReferencedEntityType,
    isItemSelected,
    allSelected,
    someSelected,
    toggleAll,
    toggleItem,
    isReferenceWithoutEntityType,
    onPlusClick,
    loadItems,
  };
}
